/**
 * The lifecycle flows T01–T21, as data — 13 Oct 2026.
 *
 * From the lifecycle plan ("Skida – Email Lifecycle Automation") and the design
 * canvas. One definition per flow: what starts it, the blocks it runs through,
 * and the facts the list screen shows. See docs/EMAIL_AUTOMATION.md.
 *
 * ## One file, two programs
 *
 * The engine in this package runs these, and the CRM's screens draw them — the
 * list, the canvas, the simulator. The client imports this file by path rather
 * than keeping a copy, because two copies of a flow are one edit away from the
 * canvas showing a condition the engine does not check. So this file has **no
 * imports** and nothing in it may reach for Node or the browser.
 *
 * ## What lives here and what lives in Firestore
 *
 * The flow's shape is code: reviewed, versioned, tested. What people change in
 * the CRM — on/off, which template it sends, settings overrides — is stored in
 * `skidaCrmAutomations/{id}` and read beside this. Everything starts off.
 */

/** Things that happen to a person, already normalised from their source. */
export type DomainEvent =
  // Reach the CRM today: RevenueCat, account creation, and the app trial
  // (appTrials.ts, every 30 minutes) for the three trial events.
  | 'account.created'
  | 'trial.started'
  | 'trial.converted'
  | 'trial.expired'
  | 'subscription.started'
  | 'subscription.renewed'
  | 'subscription.cancelled'
  | 'subscription.uncancelled'
  | 'subscription.expired'
  | 'billing.issue'
  | 'billing.recovered'
  // From the product's own records, once a night (automation/shares.ts).
  | 'route.shared'
  // Need the app event pipeline, which does not exist yet.
  | 'route.suggested'
  | 'offline.downloaded'
  | 'flyover.played'
  | 'app.opened'
  | 'guide.signal'

/** The events that have a source today. Everything else waits for the app. */
export const WIRED_EVENTS: readonly DomainEvent[] = [
  'account.created',
  'trial.started',
  'trial.converted',
  'trial.expired',
  'subscription.started',
  'subscription.renewed',
  'subscription.cancelled',
  'subscription.uncancelled',
  'subscription.expired',
  'billing.issue',
  'billing.recovered',
  // From the recipient's end, once a night — automation/shares.ts, 22 Oct 2026.
  'route.shared',
]

/** How each event reads on screen. The interface never shows a raw name. */
export const EVENT_LABELS: Record<DomainEvent, string> = {
  'account.created': 'Creates an account',
  // Store trials (RevenueCat) and, since 26 Oct 2026, Skida 3.0's own app trial.
  'trial.started': 'A trial starts (store or app)',
  'trial.converted': 'Buys Premium during the trial',
  'trial.expired': 'The trial ends without buying',
  'subscription.started': 'Starts Premium',
  'subscription.renewed': 'Renews Premium',
  'subscription.cancelled': 'Turns off auto-renew',
  'subscription.uncancelled': 'Turns auto-renew back on',
  'subscription.expired': 'Premium expires',
  'billing.issue': 'Payment fails',
  'billing.recovered': 'Payment recovers',
  'route.suggested': 'Gets a route suggestion',
  'offline.downloaded': 'Downloads an offline area',
  'flyover.played': 'Plays a 3D flyover',
  'route.shared': 'Shares a route or collection',
  'app.opened': 'Opens the app',
  'guide.signal': 'Shows a guide signal',
}

export type Plan = 'trial' | 'free' | 'premium' | 'pro'

/** A test on the contact, checked at the moment the block runs. */
export type Rule =
  | { field: 'plan'; op: 'is' | 'is-not'; value: Plan }
  | { field: 'autoRenew' | 'billingIssue' | 'marketingConsent'; op: 'is'; value: boolean }
  | { field: 'event'; op: 'not-since-start'; value: DomainEvent }
  | { field: 'sent'; op: 'not-within-days'; value: string; days: number }
  | { field: 'inactiveDays'; op: 'at-least' | 'at-most'; value: number }
  /** Days left of the trial this run belongs to — 31 Oct 2026. See `Run.endsAtMs`. */
  | { field: 'trialEndsInDays'; op: 'at-least'; value: number }

export type Block =
  | { id: string; kind: 'wait'; hours: number }
  | { id: string; kind: 'wait'; until: 'send-window' }
  | { id: string; kind: 'condition'; label: string; rules: Rule[]; no: string }
  | { id: string; kind: 'batch'; label: string }
  | { id: string; kind: 'send'; subject: string }
  | { id: string; kind: 'task'; label: string }
  | { id: string; kind: 'exit'; reason: string }

export type Trigger =
  /**
   * An event, optionally shifted. `from: 'expiresAt'` anchors on the end date
   * the event carries (a trial's end, a subscription's renewal) rather than on
   * when the event arrived — T05 is 24 h before the trial ends, whenever that is.
   */
  | { kind: 'event'; event: DomainEvent; offsetHours?: number; from?: 'event' | 'expiresAt' }
  /** A calendar date per hemisphere, every year (MM-DD). */
  | { kind: 'date'; north: string; south: string }
  /** Days before the season starts, per hemisphere. */
  | { kind: 'before-season'; days: number }
  /** First Thursday of each month while the season runs. */
  | { kind: 'monthly-in-season' }
  /** No `app.opened` for this many days, in season. */
  | { kind: 'inactive'; days: number }

export type FlowType = 'transactional' | 'lifecycle' | 'marketing'
/** Which choice on the preference page turns this email off. */
export type FlowStream = 'automation' | 'newsletter'
export type Folder = 'Trial' | 'Free' | 'Premium' | 'Seasonal' | 'Re-engagement' | 'PRO'
export type Reentry = 'user' | 'trial' | 'subscription' | 'season'

export interface Flow {
  id: string
  name: string
  folder: Folder
  type: FlowType
  stream: FlowStream
  /** Rollout phase in the plan. */
  phase: 1 | 2 | 3
  /** The trigger in plain words, for the list. */
  summary: string
  trigger: Trigger
  reentry: Reentry
  blocks: Block[]
  /**
   * About buying or keeping Premium — 26 Oct 2026. A Skida PRO member has
   * Premium with PRO, so a run of this flow for one ends before it sends.
   * The trial tips too, by the owner's decision the same day.
   */
  notForPro?: true
  /**
   * Sent unless the person has said no — 27 Oct 2026, by the owner's decision,
   * for T01 Welcome only. Most 3.0 accounts were never asked the consent
   * question, and a welcome to an account just made is where it is asked. An
   * explicit no (`emailSubscription: false`, or the preference page's "none")
   * still holds.
   */
  unlessRefused?: true
  /**
   * One of the emails sent during the trial — 31 Oct 2026 (T01, T03, T05).
   * The owner's rule: never two of them on the same day. These count only
   * against the cap of one a day, never the two-in-seven-days one — which
   * would otherwise push "trial ends tomorrow" past the end of a 7-day trial
   * — and they do not count toward other flows' weekly cap either.
   */
  trialSeries?: true
  /**
   * Skipped rather than delayed when another email went out in the last 24
   * hours — 31 Oct 2026, for T03. A delayed tip can land on the day of
   * "trial ends tomorrow"; a skipped one cannot.
   */
  skipIfCapped?: true
}

/** Every send in the plan is preceded by a check that it still makes sense. */
const window = (id = 'window'): Block => ({ id, kind: 'wait', until: 'send-window' })
const send = (subject: string): Block => ({ id: 'send', kind: 'send', subject })
const sent = (reason = 'Email sent'): Block => ({ id: 'done', kind: 'exit', reason })

export const FLOWS: Flow[] = [
  {
    id: 'T01',
    name: 'Welcome',
    folder: 'Trial',
    type: 'lifecycle',
    stream: 'automation',
    phase: 1,
    summary: 'Completes onboarding in the app',
    trigger: { kind: 'event', event: 'account.created' },
    reentry: 'user',
    unlessRefused: true,
    trialSeries: true,
    // Every new account, whatever its plan — Bjarte, 14 Oct 2026. The plan's
    // "only if still on the trial" would have welcomed almost nobody: the trial
    // starts later, in the store, not when the account is made. The cap still
    // applies; it sends at once.
    //
    // Since 27 Oct 2026 `account.created` means onboarding completed in the app
    // (`users.appOnboarding.completed`), read by `appTrials.ts`, and consent is
    // "unless refused" — see `unlessRefused`.
    blocks: [send('Welcome to Skida, {{firstname|there}}'), sent()],
  },
  // T02 First route suggestion and T04 See it in 3D — retired 31 Oct 2026 by
  // the owner: too many emails in the trial. The trial keeps T01, T03, T05,
  // and after it T06 and T07. The ids are not reused; runs still waiting end
  // as "Unknown flow".
  {
    id: 'T03',
    notForPro: true,
    trialSeries: true,
    skipIfCapped: true,
    name: 'Plan and go offline',
    folder: 'Trial',
    type: 'lifecycle',
    stream: 'automation',
    phase: 2,
    summary: 'Trial day 3 · only if the trial has 2+ days left',
    trigger: { kind: 'event', event: 'trial.started', offsetHours: 72 },
    reentry: 'trial',
    // Only when "trial ends tomorrow" (T05) falls on a later day — 31 Oct
    // 2026. In a trial of four days or fewer T03 would land on T05's day or
    // after the end, so it is left out and T05 goes on its own day.
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if the trial has 2 or more days left', rules: [{ field: 'trialEndsInDays', op: 'at-least', value: 2 }], no: 'short' },
      send('No signal on the ridge? No problem'),
      sent(),
      { id: 'short', kind: 'exit', reason: 'Trial too short: T05 goes instead' },
    ],
  },
  {
    id: 'T05',
    notForPro: true,
    trialSeries: true,
    name: 'Trial ends tomorrow',
    folder: 'Trial',
    type: 'lifecycle',
    stream: 'automation',
    phase: 1,
    summary: '1 day before the trial ends',
    trigger: { kind: 'event', event: 'trial.started', offsetHours: -24, from: 'expiresAt' },
    reentry: 'trial',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if plan is not Premium', rules: [{ field: 'plan', op: 'is-not', value: 'premium' }], no: 'premium' },
      send('Your Premium access ends tomorrow'),
      sent(),
      { id: 'premium', kind: 'exit', reason: 'Already Premium' },
    ],
  },
  {
    id: 'T06',
    notForPro: true,
    name: 'You’re on Free now',
    folder: 'Trial',
    type: 'lifecycle',
    stream: 'automation',
    phase: 1,
    summary: '1 day after the trial ends · still Free',
    trigger: { kind: 'event', event: 'trial.expired', offsetHours: 24 },
    reentry: 'trial',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if still on Free', rules: [{ field: 'plan', op: 'is', value: 'free' }], no: 'paid' },
      send('You’re on Skida Free – here’s what you keep'),
      sent(),
      { id: 'paid', kind: 'exit', reason: 'Not on Free any more' },
    ],
  },
  {
    id: 'T07',
    notForPro: true,
    name: 'Last nudge',
    folder: 'Trial',
    type: 'marketing',
    stream: 'newsletter',
    phase: 2,
    summary: '7 days after the trial ends · still Free',
    trigger: { kind: 'event', event: 'trial.expired', offsetHours: 24 * 7 },
    reentry: 'trial',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if still on Free', rules: [{ field: 'plan', op: 'is', value: 'free' }], no: 'paid' },
      send('Planning your next tour?'),
      sent(),
      { id: 'paid', kind: 'exit', reason: 'Not on Free any more' },
    ],
  },
  // T08 Free quota used — retired 14 Oct 2026: route suggestions are behind the
  // paywall, so there is no free quota. The id is not reused.
  {
    id: 'T09',
    name: 'Monthly in-season digest',
    folder: 'Free',
    type: 'marketing',
    stream: 'newsletter',
    phase: 3,
    summary: 'First Thursday of each month in season',
    trigger: { kind: 'monthly-in-season' },
    reentry: 'season',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if active in the last 90 days', rules: [{ field: 'inactiveDays', op: 'at-most', value: 90 }], no: 'skip' },
      { id: 'batch', kind: 'batch', label: 'Send in batches, most active first' },
      window(),
      send('{{month}} in your region: routes and conditions'),
      sent(),
      { id: 'skip', kind: 'exit', reason: 'Not active lately' },
    ],
  },
  {
    id: 'T10',
    name: 'Welcome to Premium',
    folder: 'Premium',
    type: 'transactional',
    stream: 'automation',
    phase: 1,
    summary: 'Starts Premium',
    trigger: { kind: 'event', event: 'subscription.started' },
    reentry: 'subscription',
    blocks: [send('You’re all set with Skida Premium'), sent()],
  },
  {
    id: 'T11',
    name: 'Bring your partners',
    folder: 'Premium',
    type: 'lifecycle',
    stream: 'automation',
    phase: 2,
    summary: '5 days after starting Premium',
    trigger: { kind: 'event', event: 'subscription.started', offsetHours: 24 * 5 },
    reentry: 'subscription',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if nothing shared yet', rules: [{ field: 'event', op: 'not-since-start', value: 'route.shared' }], no: 'skip' },
      send('Plan the next tour together'),
      sent(),
      { id: 'skip', kind: 'exit', reason: 'Already shares' },
    ],
  },
  {
    id: 'T12',
    name: 'Renewal coming up',
    folder: 'Premium',
    type: 'transactional',
    stream: 'automation',
    phase: 2,
    summary: '14 days before a yearly renewal',
    trigger: { kind: 'event', event: 'subscription.renewed', offsetHours: -24 * 14, from: 'expiresAt' },
    reentry: 'subscription',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if auto-renew is on', rules: [{ field: 'autoRenew', op: 'is', value: true }], no: 'off' },
      send('Your Premium renews on {{renews}}'),
      sent(),
      { id: 'off', kind: 'exit', reason: 'Auto-renew is off' },
    ],
  },
  {
    id: 'T13',
    name: 'Payment problem',
    folder: 'Premium',
    type: 'transactional',
    stream: 'automation',
    phase: 1,
    summary: 'Payment fails',
    trigger: { kind: 'event', event: 'billing.issue' },
    reentry: 'subscription',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if the payment is still failing', rules: [{ field: 'billingIssue', op: 'is', value: true }], no: 'fixed' },
      send('Action needed: update your payment'),
      sent(),
      { id: 'fixed', kind: 'exit', reason: 'Payment recovered' },
    ],
  },
  {
    id: 'T14',
    name: 'Cancellation',
    folder: 'Premium',
    type: 'lifecycle',
    stream: 'automation',
    phase: 2,
    summary: 'Turns off auto-renew',
    trigger: { kind: 'event', event: 'subscription.cancelled', offsetHours: 24 },
    reentry: 'subscription',
    blocks: [
      window(),
      { id: 'check', kind: 'condition', label: 'Only if auto-renew is still off', rules: [{ field: 'autoRenew', op: 'is', value: false }], no: 'back' },
      send('Sorry to see you go'),
      sent(),
      { id: 'back', kind: 'exit', reason: 'Turned auto-renew back on' },
    ],
  },
  {
    id: 'T15',
    notForPro: true,
    name: 'Win-back',
    folder: 'Re-engagement',
    type: 'marketing',
    stream: 'newsletter',
    phase: 3,
    summary: '21 days before the season · Premium expired',
    trigger: { kind: 'before-season', days: 21 },
    reentry: 'season',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if Premium has expired', rules: [{ field: 'plan', op: 'is', value: 'free' }], no: 'skip' },
      { id: 'batch', kind: 'batch', label: 'Send in batches, most active first' },
      window(),
      send('Your season is coming – come back to Premium'),
      sent(),
      { id: 'skip', kind: 'exit', reason: 'Already Premium' },
    ],
  },
  {
    id: 'T16',
    notForPro: true,
    name: 'Season kick-off',
    folder: 'Seasonal',
    type: 'marketing',
    stream: 'newsletter',
    phase: 1,
    summary: 'Season start date · sent in batches',
    trigger: { kind: 'date', north: '11-01', south: '05-01' },
    reentry: 'season',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if no T20 in the last 14 days', rules: [{ field: 'sent', op: 'not-within-days', value: 'T20', days: 14 }], no: 'recent' },
      { id: 'batch', kind: 'batch', label: 'Send in batches, most active first' },
      window(),
      send('The new season starts now'),
      sent(),
      { id: 'recent', kind: 'exit', reason: 'Got T20 recently' },
    ],
  },
  // T17 Forecast is live — retired 14 Oct 2026: the app stores no home region,
  // so there is no region whose first forecast could start it. Id not reused.
  {
    id: 'T18',
    name: 'Season wrap',
    folder: 'Seasonal',
    type: 'marketing',
    stream: 'newsletter',
    phase: 3,
    summary: 'Season end date',
    trigger: { kind: 'date', north: '05-25', south: '10-25' },
    reentry: 'season',
    blocks: [{ id: 'batch', kind: 'batch', label: 'Send in batches, most active first' }, window(), send('Thanks for a great season'), sent()],
  },
  {
    id: 'T19',
    name: 'In-season inactive',
    folder: 'Re-engagement',
    type: 'lifecycle',
    stream: 'newsletter',
    phase: 3,
    summary: 'No app visit for 21 days in season',
    trigger: { kind: 'inactive', days: 21 },
    reentry: 'season',
    blocks: [window(), send('Conditions have changed in your region'), sent()],
  },
  {
    id: 'T20',
    name: 'Dormant pre-season',
    folder: 'Re-engagement',
    type: 'marketing',
    stream: 'newsletter',
    phase: 3,
    summary: '30 days before the season · away 180+ days',
    trigger: { kind: 'before-season', days: 30 },
    reentry: 'season',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if away 180+ days', rules: [{ field: 'inactiveDays', op: 'at-least', value: 180 }], no: 'skip' },
      { id: 'batch', kind: 'batch', label: 'Send in batches, most active first' },
      window(),
      send('Ready for another season?'),
      sent(),
      { id: 'skip', kind: 'exit', reason: 'Active recently' },
    ],
  },
  {
    id: 'T21',
    name: 'Guide signal',
    folder: 'PRO',
    type: 'lifecycle',
    stream: 'newsletter',
    phase: 3,
    summary: 'Shows a guide signal',
    trigger: { kind: 'event', event: 'guide.signal' },
    reentry: 'user',
    blocks: [
      { id: 'check', kind: 'condition', label: 'Only if not already a PRO customer', rules: [{ field: 'plan', op: 'is-not', value: 'pro' }], no: 'pro' },
      { id: 'task', kind: 'task', label: 'Create a lead task for PRO sales' },
      window(),
      send('Guiding? Meet Skida PRO'),
      sent(),
      { id: 'pro', kind: 'exit', reason: 'Already a PRO customer' },
    ],
  },
]

export const FOLDERS: Folder[] = ['Trial', 'Free', 'Premium', 'Seasonal', 'Re-engagement', 'PRO']

/** The events a flow needs, from its trigger and its conditions. */
export function eventsOf(flow: Flow): DomainEvent[] {
  const out = new Set<DomainEvent>()
  if (flow.trigger.kind === 'event') out.add(flow.trigger.event)
  if (flow.trigger.kind === 'inactive') out.add('app.opened')
  for (const b of flow.blocks) {
    if (b.kind !== 'condition') continue
    for (const r of b.rules) {
      if (r.field === 'event') out.add(r.value)
      if (r.field === 'inactiveDays') out.add('app.opened')
    }
  }
  return [...out]
}

/** Whether every event the flow needs has a source today. */
export const isWired = (flow: Flow): boolean =>
  eventsOf(flow).every((e) => WIRED_EVENTS.includes(e))

/** The flows sent during the trial, which share the one-a-day rule. */
export const TRIAL_SERIES = new Set(FLOWS.filter((f) => f.trialSeries).map((f) => f.id))
